import { Controller } from '@hotwired/stimulus'
import Helpers from '../../helpers'

export default class NewInvestmentFlowSubscriptionController extends Controller {
  static targets = [
    'amount',
    'amountDisplay',
    'submitButton',
    'subscriptionFormContent',
    'bonusFormContent',
    'bonusCodeInput',
    'bonusCodeField',
    'addBonusCodeButton',
    'bonusAppliedMessage',
    'bonusRate',
    'modal',
  ];
  static values = {
    bonusCodeValidationUrl: String,
  }

  resetForm() {
    this.removeBonusCode();
    this.amountTarget.value = '';
    this.amountTarget.dispatchEvent(new Event('input'));
    this.backToSubscriptionForm();
  }

  focusAmount() {
    this.amountDisplayTarget.focus();
  }

  checkAmountAndEnableSubmitButton() {
    // Read the hidden numeric field: the display input holds the fr-FR formatted
    // value ("1 234 €"), which parseInt would truncate at the thousands separator.
    const amount = parseInt(this.amountTarget.value, 10);
    const maxAmount = parseInt(this.amountTarget.dataset.maxValue, 10);
    this.submitButtonTarget.disabled = isNaN(amount) || amount <= 0 || amount > maxAmount;

    // #subscribedAmount lives in the confirm modal, moved to body by the modal
    // portal, hence the global lookup instead of a Stimulus target.
    $('#subscribedAmount').html(isNaN(amount) ? '' : Helpers.integerCurrencyFormater.format(amount));
  }

  displayBonusCodeForm() {
    this.subscriptionFormContentTarget.classList.add('d-none');
    this.bonusFormContentTarget.classList.remove('d-none');
  }

  backToSubscriptionForm() {
    this.bonusFormContentTarget.classList.add('d-none');
    this.subscriptionFormContentTarget.classList.remove('d-none');
  }

  async checkAndApplyBonusCode() {
    const bonusCode = this.bonusCodeInputTarget.value.trim();
    const result = bonusCode ? await this.validateBonusCode(bonusCode) : { valid: false };

    if (result.valid) {
      if (this.hasBonusCodeFieldTarget) {
        this.bonusCodeFieldTarget.value = bonusCode;
      }
      this.bonusCodeInputTarget.classList.remove('is-invalid');
      this.displayAppliedBonusCode(result.bonusRate);
      this.backToSubscriptionForm();
    } else {
      this.bonusCodeInputTarget.classList.add('is-invalid');
    }
  }

  displayAppliedBonusCode(bonusRate) {
    if (this.hasBonusRateTarget) {
      this.bonusRateTarget.textContent = bonusRate;
    }
    this.setBonusApplied(true);
  }

  removeBonusCode() {
    if (this.hasBonusCodeFieldTarget) {
      this.bonusCodeFieldTarget.value = '';
    }
    this.bonusCodeInputTarget.value = '';
    this.setBonusApplied(false);
  }

  setBonusApplied(applied) {
    if (this.hasBonusAppliedMessageTarget) {
      this.bonusAppliedMessageTarget.classList.toggle('d-none', !applied);
      this.bonusAppliedMessageTarget.classList.toggle('d-flex', applied);
    }
    if (this.hasAddBonusCodeButtonTarget) {
      this.addBonusCodeButtonTarget.classList.toggle('d-none', applied);
    }
  }

  async validateBonusCode(bonusCode) {
    const url = new URL(this.bonusCodeValidationUrlValue, window.location.origin);
    url.searchParams.set('code', bonusCode);

    try {
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const json = await response.json();
      return { valid: json.valid === true, bonusRate: json.bonus_rate };
    } catch (e) {
      console.error('Error validating bonus code:', e);
      return { valid: false };
    }
  }

  // The confirm modal submit button belongs to this form: without this, Enter would implicitly submit through it,
  // skipping both the amount check and the confirmation step.
  continueWithEnter(event) {
    event.preventDefault();

    if (!this.submitButtonTarget.disabled) {
      this.submitButtonTarget.click();
    }
  }

  closeOffcanvas() {
    if (parseInt(this.amountTarget.value, 10) > 0) {
      bootstrap.Offcanvas.getOrCreateInstance(this.element).hide();
    }
  }
}
